import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDrop } from "react-dnd";

import {
  setCurrentLayout,
  setBlocksType,
  addFeedToCurrentLayout,
} from "actions";
import { TEMPLATE_TYPES } from "constants";
import { SVG_ICONS } from "../../config/Common";
import {
  SVG_DELETE_ICON,
  SVG_DOUBLE_UP_ICON,
  SVG_UP_ICON,
  SVG_DUPLICATE_ICON,
} from "../../services/svgIcones";
import {
  replaceContentFeed,
  replaceUnreferencedBlock,
} from "../../services/block/newsletter";
import { FAKE_FEEDS } from "../../services/block/helpers";
import { isArraysEqual } from "../../services/utils";

const FeedCard = memo(
  ({ card, index, moveCard, removeCard, duplicateCard, lastIndex }) => {
    const [prepareToRemove, setPrepareToRemove] = useState(false);
    const [highlighted, setHighlighted] = useState(false);
    const [feedsIds, setFeedsIds] = useState([]);

    const iframeRef = useRef(null);

    const dispatch = useDispatch();
    const currentLayout = useSelector(
      (state) => state.themes.newsletter.currentLayout,
    );
    const blockType = useSelector((state) => state.themes.blocks.filter.type);

    const { id, column, feeds, type, content } = card;

    // Setup drop target using useDrop hook
    const [{ isOver, canDrop }, dropRef] = useDrop(
      () => ({
        accept: type, // Accept items matching this card's type
        canDrop: () => {
          // Only allow drop if there's room in the card
          const maxColumn = column || 1;
          return feeds.length < maxColumn;
        },
        drop: (item) => {
          // Add the dropped feed to the current layout
          dispatch(setCurrentLayout(card));
          dispatch(addFeedToCurrentLayout(item));
        },
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }),
      }),
      [card, column, feeds, type, dispatch],
    );

    useEffect(() => {
      const newFeedsIds = feeds.map(({ id }) => id);

      if (!isArraysEqual(newFeedsIds, feedsIds)) {
        const elem = document.getElementById(`card-${id}`);
        if (elem) {
          elem.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => setHighlighted(false), 300);
          setFeedsIds(newFeedsIds);
          setHighlighted(true);
        }
      }
    }, [feeds, feedsIds, id]);

    const toogleSelection = useCallback(
      (e) => {
        if (
          [
            TEMPLATE_TYPES["NEWS_LAYOUT"],
            TEMPLATE_TYPES["SPEAKER_LAYOUT"],
            TEMPLATE_TYPES["SLOT_LAYOUT"],
            TEMPLATE_TYPES["FORMATION_LAYOUT"],
          ].indexOf(type) === -1
        ) {
          return;
        }
        dispatch(
          setCurrentLayout(
            currentLayout && currentLayout.id === id ? null : card,
          ),
        );
        if (type !== blockType) {
          dispatch(setBlocksType(type));
        }
        e.preventDefault();
        e.stopPropagation();
      },
      [blockType, card, currentLayout, dispatch, id, type],
    );

    const resizeIframe = useCallback(() => {
      const refId = `iframe-${card.id}`;
      let previewContainer = document.getElementById(refId);
      if (previewContainer) {
        let iframeElement = iframeRef.current;
        if (iframeElement && iframeElement.contentWindow) {
          let newHeight =
            iframeElement.contentWindow.document.body.offsetHeight;
          previewContainer.style.height = `${newHeight * 0.55}px`;
          iframeElement.contentWindow.scrollTo(
            (iframeElement.contentDocument.body.scrollWidth -
              iframeElement.contentDocument.body.clientWidth) /
              2,
            0,
          );
        }
      }
    }, [card.id]);

    const handleRemoveCard = useCallback(() => {
      setPrepareToRemove(true);
      setTimeout(() => {
        removeCard();
      }, 500);
    }, [removeCard]);

    let preview;
    let maxColumn = column || 1;

    let cssClass = `${
      feeds && feeds.length === maxColumn ? "" : "border-alert"
    }`;
    if (currentLayout && currentLayout.id === id) {
      cssClass = "active";
    }
    // Add visual feedback for drag over
    if (isOver && canDrop) {
      cssClass += " drag-over";
    } else if (isOver && !canDrop) {
      cssClass += " drag-rejected";
    }
    const ref = `iframe-${id}`;

    if (
      [
        TEMPLATE_TYPES["STATIC_BLOCK"],
        TEMPLATE_TYPES["UNREFERENCED_BLOCK"],
      ].indexOf(type) > -1
    ) {
      preview = (
        <div key={ref} id={ref}>
          <iframe
            scrolling="no"
            className="unreferenced"
            srcDoc={`<body style='margin: 0;'>${replaceUnreferencedBlock(
              content,
            )}</body>`}
            ref={iframeRef}
            onLoad={resizeIframe}
          />
        </div>
      );
    } else {
      const data = [...new Array(maxColumn)].map(
        (_, index) => feeds[index] || FAKE_FEEDS[type],
      );

      const style = maxColumn === 1 ? { position: "absolute" } : {};
      let replacedContent = `<body style='margin: 0;'>${replaceContentFeed(
        content,
        data,
      )}</body>`;
      preview = (
        <div
          className={"iframe-container " + cssClass}
          key={ref}
          onClick={toogleSelection}
          id={ref}
        >
          <iframe
            style={style}
            srcDoc={replacedContent}
            ref={iframeRef}
            onLoad={resizeIframe}
            scrolling="no"
          />
          <img src={`${"/img/icons/" + SVG_ICONS["ARROW_LEFT"]}`} alt="" />
        </div>
      );
    }

    return (
      <div
        id={`card-${id}`}
        ref={dropRef}
        className={`layout-item flex-container ${
          prepareToRemove ? "remove" : ""
        } ${highlighted ? "highlight" : ""} ${isOver ? "drop-target" : ""}`}
      >
        <div className="card-actions">
          {index > 1 && (
            <div className="double-icon-top" onClick={() => moveCard(index, 0)}>
              <span className={"double-up"}>{SVG_DOUBLE_UP_ICON}</span>
            </div>
          )}
          {index !== 0 && (
            <div
              className="single-icon"
              onClick={() => moveCard(index, index - 1)}
            >
              <span className={"up"}>{SVG_UP_ICON}</span>
            </div>
          )}
          {index !== lastIndex && (
            <div onClick={() => moveCard(index, index + 1)}>
              <span className={"down"}>{SVG_UP_ICON}</span>
            </div>
          )}
          {index < lastIndex - 1 && (
            <div
              className="double-icon-bottom"
              onClick={() => moveCard(index, lastIndex)}
            >
              <span className="double-down">{SVG_DOUBLE_UP_ICON}</span>
            </div>
          )}
        </div>
        <div className="card-actions float-right">
          <div onClick={handleRemoveCard}>{SVG_DELETE_ICON}</div>
          <div onClick={duplicateCard}>{SVG_DUPLICATE_ICON}</div>
        </div>
        <div className={`layout-item__img`}>{preview}</div>
      </div>
    );
  },
);

FeedCard.displayName = "FeedCard";

export default FeedCard;
